import type { ReactNode } from 'react';
import { LayoutDashboard, Bot, Users, LogOut, ShoppingCart, CreditCard } from 'lucide-react';
import { storage } from '@/services/storage';
import type { AdminRoute } from './AdminApp';

interface Props {
  active: AdminRoute;
  onNavigate: (to: AdminRoute) => void;
  onLogout: () => void;
  children: ReactNode;
}

// AdminLayout is the portal chrome: branded sidebar + top bar with the
// admin's email and a logout button. All admin pages render inside the
// `main` slot.
export function AdminLayout({ active, onNavigate, onLogout, children }: Props) {
  const user = storage.getUser<{ email?: string }>();
  return (
    <div className="flex min-h-screen bg-surface text-text-primary">
      <aside className="flex w-56 flex-col border-r border-border bg-white">
        <div className="flex items-center gap-2 border-b border-border px-4 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-bold">
            EA
          </div>
          <div className="text-sm font-semibold">Excel Autopilot</div>
        </div>
        <nav className="flex-1 space-y-1 p-2">
          <NavItem label="Dashboard" icon={<LayoutDashboard className="h-4 w-4" />}
            active={active === 'dashboard'} onClick={() => onNavigate('dashboard')} />
          <NavItem label="AI Configuration" icon={<Bot className="h-4 w-4" />}
            active={active === 'ai-config'} onClick={() => onNavigate('ai-config')} />
          <NavItem label="Users" icon={<Users className="h-4 w-4" />}
            active={active === 'users'} onClick={() => onNavigate('users')} />
          <NavItem label="Payments" icon={<CreditCard className="h-4 w-4" />}
            active={active === 'payments'} onClick={() => onNavigate('payments')} />
          <NavItem label="Enrollment" icon={<ShoppingCart className="h-4 w-4" />}
            active={active === 'enrollment'} onClick={() => onNavigate('enrollment')} />
        </nav>
        <div className="border-t border-border p-3 text-xs text-text-secondary">
          <div className="mb-2 truncate" title={user?.email}>{user?.email ?? '—'}</div>
          <button type="button" onClick={onLogout} className="btn-ghost w-full justify-start">
            <LogOut className="mr-2 h-3.5 w-3.5" />
            Log out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto p-6">
        {children}
      </main>
    </div>
  );
}

function NavItem({
  label, icon, active, onClick,
}: { label: string; icon: ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition',
        active
          ? 'bg-primary/10 text-primary font-medium'
          : 'text-text-primary hover:bg-black/5',
      ].join(' ')}
    >
      {icon}
      {label}
    </button>
  );
}
